import {
  HINTS_PER_QUESTION,
  ROUND_MODES,
  ROUND_STATUSES,
  answerResponseSchema,
  questionContentSchema,
} from '@oefy/core';
import { z } from 'zod';
import { gameKeySchema, schoolGroupSchema } from './common';
import {
  difficultySchema,
  questionTypeSchema,
  subjectFormatSchema,
  subjectKeySchema,
} from './content';

export const roundModeSchema = z.enum(ROUND_MODES);

export const dailyGoalSchema = z.object({
  completed: z.int().min(0),
  target: z.int().min(1),
  reached: z.boolean(),
});

export const openRoundSchema = z.object({
  id: z.uuid(),
  subjectKey: z.string(),
  subjectName: z.string(),
  mode: roundModeSchema,
  topicName: z.string().nullable(),
  subtopicId: z.uuid().nullable(),
  subtopicName: z.string().nullable(),
  difficulty: difficultySchema.nullable(),
  answered: z.int().min(0),
  total: z.int().min(1),
});

export const playableSubjectSchema = z.object({
  key: z.string(),
  name: z.string(),
  format: subjectFormatSchema,
});

export const playOverviewQuerySchema = z.object({
  schoolGroup: z.coerce.number().pipe(schoolGroupSchema),
});

export const playOverviewSchema = z.object({
  schoolGroup: schoolGroupSchema,
  subjects: z.array(playableSubjectSchema),
  /** Games switched on for this group; the dashboard hides the Spelletjes tile when empty. */
  games: z.array(gameKeySchema),
  dailyGoal: dailyGoalSchema,
  openRound: openRoundSchema.nullable(),
});

export const topicLevelSchema = z.object({
  difficulty: difficultySchema,
  available: z.boolean(),
});

export const practiceSubtopicSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  levels: z.array(topicLevelSchema),
});

export const practiceTopicSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  subtopics: z.array(practiceSubtopicSchema),
});

export const subjectPlanSchema = z.object({
  subject: playableSubjectSchema,
  schoolGroup: schoolGroupSchema,
  topics: z.array(practiceTopicSchema),
  openRound: openRoundSchema.nullable(),
});

export const subjectPlanParamsSchema = z.object({
  childId: z.uuid(),
  subjectKey: subjectKeySchema,
});

export const childParamsSchema = z.object({ childId: z.uuid() });
export const roundParamsSchema = z.object({ roundId: z.uuid() });

export const startRoundSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('practice'),
    subjectKey: subjectKeySchema,
    schoolGroup: schoolGroupSchema,
    subtopicId: z.uuid(),
    difficulty: difficultySchema,
    replaceOpen: z.boolean().default(false),
  }),
  z.object({
    mode: z.literal('test'),
    subjectKey: subjectKeySchema,
    schoolGroup: schoolGroupSchema,
    replaceOpen: z.boolean().default(false),
  }),
]);

export const presentedQuestionSchema = z.object({
  id: z.uuid(),
  type: questionTypeSchema,
  instruction: z.string().nullable(),
  context: z.string().nullable(),
  prompt: z.string(),
  imageKey: z.string().nullable(),
  imageAlt: z.string().nullable(),
  content: questionContentSchema,
});

export const readingTextViewSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  preview: z.string(),
  body: z.string(),
  imageKey: z.string().nullable(),
  imageAlt: z.string().nullable(),
});

export const roundProgressSchema = z.object({
  answered: z.int().min(0),
  total: z.int().min(1),
  correct: z.int().min(0),
  incorrect: z.int().min(0),
});

export const roundResultSchema = z.object({
  prompt: z.string(),
  correct: z.boolean(),
});

export const roundViewSchema = z.object({
  id: z.uuid(),
  childId: z.uuid(),
  mode: roundModeSchema,
  subjectKey: z.string(),
  subjectName: z.string(),
  topicId: z.uuid().nullable(),
  topicName: z.string().nullable(),
  subtopicId: z.uuid().nullable(),
  subtopicName: z.string().nullable(),
  difficulty: difficultySchema.nullable(),
  schoolGroup: schoolGroupSchema,
  status: z.enum(ROUND_STATUSES),
  progress: roundProgressSchema,
  question: presentedQuestionSchema.nullable(),
  readingText: readingTextViewSchema.nullable(),
  startsNewText: z.boolean(),
  hints: z.array(z.string()).max(HINTS_PER_QUESTION),
  results: z.array(roundResultSchema),
});

export const answerInputSchema = z.object({
  position: z.int().min(0),
  response: answerResponseSchema,
});

export const hintInputSchema = z.object({
  position: z.int().min(0),
  response: answerResponseSchema.nullable(),
});

export const hintSchema = z.object({
  step: z.int().min(1).max(HINTS_PER_QUESTION),
  text: z.string(),
  remaining: z.int().min(0).max(HINTS_PER_QUESTION),
});

export const answerFeedbackSchema = z.object({
  correct: z.boolean(),
  correctResponse: answerResponseSchema,
  explanation: z.string(),
  progress: roundProgressSchema,
  finished: z.boolean(),
  dailyGoal: dailyGoalSchema.extend({ reachedNow: z.boolean() }).nullable(),
});

export type DailyGoal = z.infer<typeof dailyGoalSchema>;
export type OpenRound = z.infer<typeof openRoundSchema>;
export type PlayableSubject = z.infer<typeof playableSubjectSchema>;
export type PlayOverview = z.infer<typeof playOverviewSchema>;
export type TopicLevel = z.infer<typeof topicLevelSchema>;
export type PracticeTopic = z.infer<typeof practiceTopicSchema>;
export type PracticeSubtopic = z.infer<typeof practiceSubtopicSchema>;
export type SubjectPlan = z.infer<typeof subjectPlanSchema>;
export type StartRoundInput = z.input<typeof startRoundSchema>;
export type PresentedQuestion = z.infer<typeof presentedQuestionSchema>;
export type ReadingTextView = z.infer<typeof readingTextViewSchema>;
export type RoundProgress = z.infer<typeof roundProgressSchema>;
export type RoundView = z.infer<typeof roundViewSchema>;
export type AnswerInput = z.infer<typeof answerInputSchema>;
export type HintInput = z.infer<typeof hintInputSchema>;
export type Hint = z.infer<typeof hintSchema>;
export type AnswerFeedback = z.infer<typeof answerFeedbackSchema>;
